/**
 * D03 — How It Works.
 *
 * Supporting a meal: six steps, server-supplied so the copy can be edited or
 * localised without a release. Falls back to the approved wording if the
 * request fails, because an empty explainer is worse than a slightly stale one.
 *
 * Ordering a tiffin, while the kitchen is taking orders: its own steps, told
 * with the kitchen's current terms — the schedules it offers, the fewest days
 * an order runs for, what pick & drop costs a meal, and when each meal is
 * served — so the explainer cannot promise what the order screens then
 * refuse.
 */

import { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  formatMoney,
  formatWindow,
  serviceScheduleLabel,
  type HowItWorksStep,
  type TiffinConfig,
} from '@mfa/domain';
import { colors, sizing, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card, CardRow } from '../../components/common/Card';
import { SegmentedControl } from '../../components/common/SegmentedControl';
import { ArrowIcon, Glyph, GlyphCircle } from '../../components/common/Icon';
import { LoadingState } from '../../components/feedback/States';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Text } from '../../components/common/Text';
import { useAppConfig, useHowItWorks } from '../../core/api/hooks';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

/** The approved copy, used only when the API is unreachable. */
const FALLBACK: HowItWorksStep[] = [
  { order: 1, title: 'You Choose', description: 'Select a meal and number of people', icon: 'choose' },
  { order: 2, title: 'You Pay', description: 'Make a secure payment online', icon: 'donate' },
  { order: 3, title: 'We Prepare', description: 'We cook with care and hygiene', icon: 'prepare' },
  {
    order: 4,
    title: 'We Distribute',
    description: 'Meals are distributed to those in need',
    icon: 'distribute',
  },
  {
    order: 5,
    title: 'Evidence Is Captured',
    description: 'Photos or video of the distribution, recorded as it happens',
    icon: 'camera',
  },
  {
    order: 6,
    title: 'You Get Update',
    description: 'You receive confirmation with impact',
    icon: 'update',
  },
];

type Guide = 'TIFFIN' | 'DONATION';

/** In Home's order. */
const GUIDES: Guide[] = ['TIFFIN', 'DONATION'];
const GUIDE_LABEL: Record<Guide, string> = {
  TIFFIN: 'Order a Tiffin',
  DONATION: 'Support a Meal',
};

/** A step whose words carry the kitchen's figures: `{n}` and the like. */
interface Step extends HowItWorksStep {
  values?: Record<string, string>;
}

function handOver(tiffin: TiffinConfig): string {
  const delivers = tiffin.fulfilmentTypes.includes('DOORSTEP_DELIVERY');
  const collects = tiffin.fulfilmentTypes.includes('SELF_PICKUP');
  if (delivers && collects) {
    return 'Say how many persons. We bring it to your door with pick & drop, {fee} a meal, or you collect it from our kitchen.';
  }
  if (delivers) return 'Say how many persons. We bring it to your door with pick & drop, {fee} a meal.';
  return 'Say how many persons, then collect it from our kitchen.';
}

/** The tiffin service, step by step, on the kitchen's current terms. */
function tiffinSteps(tiffin: TiffinConfig, t: (text: string) => string): Step[] {
  return [
    {
      order: 1,
      title: 'Build Your Tiffin',
      description: 'Choose breakfast, lunch or dinner, then fill one box from the menu.',
      icon: 'tiffin',
    },
    {
      order: 2,
      title: 'Choose How Often',
      description:
        'Start on any day, for at least {n} days in a row and up to {max}: {often}.',
      icon: 'calendar',
      values: {
        n: String(tiffin.minDays),
        max: String(tiffin.maxDays),
        often: tiffin.schedules.map((schedule) => t(serviceScheduleLabel[schedule])).join(' · '),
      },
    },
    {
      order: 3,
      title: 'Persons & Delivery',
      description: handOver(tiffin),
      icon: 'delivery',
      values: { fee: formatMoney(tiffin.delivery.fee) },
    },
    {
      order: 4,
      title: 'You Pay Securely',
      description:
        'One box for each person at each meal, plus pick & drop for each meal. Pay online, once for the whole order.',
      icon: 'card',
    },
    {
      order: 5,
      title: 'We Cook Fresh',
      description: 'Cooked fresh every day and brought to you, or ready to collect, at your meal time.',
      icon: 'prepare',
    },
    {
      order: 6,
      title: 'Follow Every Step',
      description: 'See your order in My Orders, from accepted to delivered, and call us any time.',
      icon: 'update',
    },
  ];
}

export function HowItWorksScreen({ navigation }: DonorScreenProps<'HowItWorks'>) {
  const t = useT();
  const { data, isLoading, isError } = useHowItWorks();
  const { data: config } = useAppConfig();
  const tiffin = config?.tiffin?.available ? config.tiffin : null;
  const [guide, setGuide] = useState<Guide>('TIFFIN');
  // Only the kitchen's own guide while it is taking orders.
  const shown: Guide = tiffin ? guide : 'DONATION';

  const donationSteps: Step[] = data ?? (isError ? FALLBACK : []);
  const steps = shown === 'TIFFIN' && tiffin ? tiffinSteps(tiffin, t) : donationSteps;
  const fill = (text: string, values?: Record<string, string>) =>
    Object.entries(values ?? {}).reduce(
      (out, [key, value]) => out.replace(`{${key}}`, value),
      t(text),
    );

  return (
    <ScreenShell
      leading="menu"
      onMenuPress={() => navigation.getParent()?.dispatch({ type: 'OPEN_DRAWER' })}
      testID="screen-how-it-works"
      title={t('How It Works')}
      subtitle={
        shown === 'TIFFIN'
          ? t('Home-style meals, cooked fresh and brought to you.')
          : t('Your kindness brings hope to someone in need.')
      }
      pinnedFooter={
        shown === 'TIFFIN' ? (
          <Button
            label={t('Order a Tiffin')}
            onPress={() => navigation.navigate('TiffinMenu')}
            leadingIcon={<Glyph name="tiffin" size={sizing.icon.sm} />}
            trailingIcon={<ArrowIcon />}
            testID="cta-guide-order-tiffin"
          />
        ) : (
          <Button
            label={t('Choose a Meal to Support')}
            onPress={() => navigation.navigate('DonateMeal')}
            trailingIcon={<ArrowIcon />}
            testID="cta-choose-meal"
          />
        )
      }
    >
      {tiffin ? (
        <SegmentedControl
          options={GUIDES}
          value={shown}
          onChange={setGuide}
          label={(option) => t(GUIDE_LABEL[option])}
          testIDPrefix="guide"
          style={styles.guides}
        />
      ) : null}

      {isLoading && steps.length === 0 ? (
        <LoadingState label={t('Loading steps')} />
      ) : (
        <View style={styles.steps} testID={`guide-steps-${shown}`}>
          {steps.map((step, index) => (
            <View key={step.order} testID={`step-${step.order}`}>
              <View style={styles.step}>
                <View style={styles.marker}>
                  <GlyphCircle name={step.icon} size={56} />
                  <Text variant="cardTitle" tone="brand" style={styles.stepNumber}>
                    {step.order}
                  </Text>
                </View>
                <View style={styles.stepBody}>
                  <Text variant="cardTitle">{t(step.title)}</Text>
                  <Text variant="body" tone="secondary">
                    {fill(step.description, step.values)}
                  </Text>
                </View>
              </View>

              {index < steps.length - 1 ? (
                <View style={styles.connector} accessibilityElementsHidden>
                  <View style={styles.connectorLine} />
                </View>
              ) : null}
            </View>
          ))}
        </View>
      )}

      {shown === 'TIFFIN' && tiffin ? (
        <Card title={t('Meal times')} style={styles.times} testID="guide-meal-times">
          {tiffin.slots
            .filter((slot) => slot.enabled)
            .map((slot) => (
              <CardRow key={slot.slot} label={t(slot.label)} value={formatWindow(slot.start, slot.end)} />
            ))}
          <Text variant="caption" tone="tertiary" style={styles.timesNote}>
            {t('Each meal stops taking orders a little before it is served; the menu shows when.')}
          </Text>
        </Card>
      ) : null}
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  guides: {
    marginBottom: space.lg,
  },
  times: {
    marginTop: space.xl,
  },
  timesNote: {
    marginTop: space.sm,
  },
  subtitle: {
    marginTop: space.xs,
    marginBottom: space.xl,
  },
  steps: {
    gap: 0,
  },
  step: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.lg,
  },
  marker: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepNumber: {
    position: 'absolute',
  },
  stepBody: {
    flex: 1,
    gap: space.xxs,
  },
  connector: {
    height: space['2xl'],
    width: 56,
    alignItems: 'center',
    justifyContent: 'center',
  },
  connectorLine: {
    width: 2,
    height: '100%',
    backgroundColor: colors.border.default,
  },
});
